// niceSteps.ts — "nice round values" slider scale (NumberField's `sliderScale` /
// `sliderSteps` props). Motivation: a plain linear <input type="range"> maps pixels to
// values 1:1, so hitting an exact round number (e.g. "10000" on a 0..200000 € slider) by
// dragging is close to impossible — a single pixel is worth hundreds of euro at that
// scale. Fix: the slider thumb doesn't move over the raw value at all, it moves over the
// INDEX of a precomputed array of round values (see buildNiceSteps below), so every
// notch the thumb can land on already IS a round number, and the notches get coarser at
// the high end (where users care less about exact euro amounts) while staying fine
// (100 €) at the low end. The paired <input type="number"> is untouched by any of
// this — it stays free-form/exact (see NumberField.tsx's handleNumber), this module only
// ever affects the range input's own snap points.
export interface NiceStepsSegment {
  /** Inclusive lower bound of this segment (must equal the previous segment's `to`, so
   * segments chain into one continuous, gap-free scale). */
  from: number;
  /** Inclusive upper bound of this segment. */
  to: number;
  /** Spacing between notches within this segment. */
  step: number;
}

/** The "money" slider scale (Einmalerlag, Sonderzahlung, Entnahme monatlich-brutto):
 * fine (100 €) up to 2 000 €, then 500 € up to 10 000 € (so 10 000 lands exactly on a
 * notch), then 1 000 € up to 50 000 €, then coarse 5 000 € up to 200 000 €. */
export const MONEY_SCALE_SEGMENTS: readonly NiceStepsSegment[] = [
  { from: 0, to: 2_000, step: 100 },
  { from: 2_000, to: 10_000, step: 500 },
  { from: 10_000, to: 50_000, step: 1_000 },
  { from: 50_000, to: 200_000, step: 5_000 },
];

/** Expands a chain of segments into one flat, strictly increasing array of round values.
 * Consecutive segments share their boundary value (e.g. segment 1 ends at 2000, segment 2
 * starts at 2000) — that shared value is only pushed once. */
export function buildNiceSteps(segments: readonly NiceStepsSegment[]): number[] {
  const steps: number[] = [];
  for (const seg of segments) {
    for (let v = seg.from; v <= seg.to; v += seg.step) {
      if (steps.length === 0 || steps[steps.length - 1] !== v) steps.push(v);
    }
  }
  return steps;
}

/** Precomputed once at module load — the array itself never changes at runtime. */
export const MONEY_STEPS: readonly number[] = buildNiceSteps(MONEY_SCALE_SEGMENTS);

/** index -> value. Out-of-range indices clamp to the first/last notch rather than
 * throwing, so a stale/out-of-sync index (e.g. from a resized `steps` array) degrades
 * gracefully instead of rendering `undefined`. */
export function indexToValue(steps: readonly number[], index: number): number {
  if (steps.length === 0) return 0;
  const i = Math.round(clampNum(index, 0, steps.length - 1));
  return steps[i]!;
}

/** value -> index of the NEAREST notch (binary search; `steps` must be sorted
 * ascending, which every array `buildNiceSteps` produces always is). Used both to drive
 * the range input's own `value` (so the thumb tracks a freely-typed, possibly off-notch
 * number input value) and, on `input`/`change`, to snap a drag back onto a notch. Exact
 * values (already on a notch) always resolve to that notch, never a neighbour. */
export function valueToNearestIndex(steps: readonly number[], value: number): number {
  if (steps.length === 0) return 0;
  if (value <= steps[0]!) return 0;
  const last = steps.length - 1;
  if (value >= steps[last]!) return last;

  let lo = 0;
  let hi = last;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (steps[mid]! < value) lo = mid + 1;
    else hi = mid;
  }
  // `lo` is the first index whose value is >= `value`; compare it against its
  // predecessor to find which of the two is actually nearer.
  if (lo > 0) {
    const prev = steps[lo - 1]!;
    const cur = steps[lo]!;
    if (value - prev <= cur - value) return lo - 1;
  }
  return lo;
}

/** Restricts a (typically the full MONEY_STEPS) scale to the notches that fall inside
 * [min, max] — so a field with a business max below the scale's own top end (e.g.
 * "Monatliche Entnahme (brutto)" capped at 10 000 €) doesn't expose slider notches the
 * field would never actually accept. Always keeps at least one notch (falls back to
 * clamp(min, ...) as a single-element array) so callers never have to special-case an
 * empty result. */
export function clampNiceSteps(steps: readonly number[], min: number, max: number): number[] {
  const filtered = steps.filter((v) => v >= min && v <= max);
  if (filtered.length > 0) return filtered;
  return [clampNum(min, min, max)];
}

function clampNum(v: number, min: number, max: number): number {
  if (!Number.isFinite(v)) return min;
  return Math.min(max, Math.max(min, v));
}
